import { MemberSetModel } from "@app/members";
import { Project } from "@app/projects";
import { Waffles, wafflesKeyColumnName } from "@app/shared/waffles";
import { Roles, UserContextProps, withUserContext } from "@app/user";
import * as React from "react";
import { RouteComponentProps } from "react-router-dom";
import { QESButton, QESSelect, QESTextInput } from "@app/shared/qes-standards";
import EditProjectOverrideMemberSet from "./editProjectOverrideMemberSet";
import { ProjectOverrideMemberSetType } from "./enumerators";
import { JSX } from "react";

interface MemberTableProps extends RouteComponentProps {
  disabled: boolean;
  generateAlternateMemberships: () => void;
  handleMemberPopAliasChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleMemberPopAliasChange2: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleMemberPopChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  handleMemberPopChange2: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  handleRiskPopChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onAddAlternateRow: () => void;
  onAlternateMemberPopAliasChange: (rows: EditProjectOverrideMemberSet[]) => void;
  onAlternateMemberPopChange: (memberPopId: number, prevMemberPopId: number) => void;
  onDeleteAlternateRow: (rows: EditProjectOverrideMemberSet[]) => void;
  project: Project;
  projectMemberPopulations: MemberSetModel[];
  shared: boolean;
  allowAlternates: boolean;
  isZipProject: boolean;
}

interface MemberWaffleToppings {
  key: string;
  MemberPop: JSX.Element;
  MemberAlias: JSX.Element;
  MemberType: JSX.Element;
  Delete: JSX.Element;
}

interface MemberTableState {
  memberData: EditProjectOverrideMemberSet[];
}

/* eslint-disable sort-keys */
class MemberTable extends React.Component<MemberTableProps & UserContextProps, MemberTableState> {
  constructor(props: MemberTableProps & UserContextProps) {
    super(props);
    this.state = {
      memberData: props.project.editProjectOverrideMemberSets,
    };
  }

  public render() {
    const records = this.buildRecords();

    const editorConfig = {
      MemberType: {
        displayText: "Member Population Type",
        noSort: true,
        testId: "member-type",
      },
      MemberPop: {
        displayText: "Member Population",
        noSort: true,
        testId: "member-pop",
      },
      MemberAlias: {
        displayText: "Alias",
        noSort: true,
        testId: "member-alias",
      },
      Delete: {
        displayText: " ",
        noSort: true,
        hidden: !this.props.allowAlternates,
        testId: "delete",
      },
    };

    const viewerConfig = {
      MemberType: {
        displayText: "Member Population Type",
        noSort: true,
        testId: "member-type",
      },
      MemberPop: {
        displayText: "Member Population",
        noSort: true,
        testId: "member-pop",
      },
      MemberAlias: {
        displayText: "Alias",
        noSort: true,
        testId: "member-alias",
      },
    };

    const columnConfig = !this.props.shared
      ? editorConfig
      : viewerConfig;

    const allAlternateMembershipsAreUsed = this.props.project.editProjectOverrideMemberSets.filter((oms) => oms.projectOverrideMemberSetType === ProjectOverrideMemberSetType.Alternate).length >= this.props.projectMemberPopulations.length;

    return (
      <React.Fragment>
        <Waffles<MemberWaffleToppings>
          columns={columnConfig}
          data={records}
          disableProcessingOverlay={true}
          testId="member-view"
        />
        {!this.props.shared && this.props.allowAlternates ?
          <QESButton
            qesTestId="member-table_add-alternate-member-population"
            qesType="secondary"
            onClick={() => this.addMemberRow()}
            disabled={this.props.disabled || allAlternateMembershipsAreUsed}
          ><i className="fa fa-plus pr5" /> Add Alternate Member Population </QESButton> :
          <div />
        }
      </React.Fragment>
    );
  }

  private addMemberRow() {
    this.props.onAddAlternateRow();
  }

  private deleteMemberRow = (row: EditProjectOverrideMemberSet) => {
    const updateRows = this.state.memberData;
    updateRows.splice(this.state.memberData.indexOf(row), 1);
    this.props.onDeleteAlternateRow(updateRows);
  }

  private updateRowAlias = (alias: string, row: EditProjectOverrideMemberSet) => {
    row.alias = alias;
    this.props.onAlternateMemberPopAliasChange(this.state.memberData);
  }

  private updateRowId = (e: React.ChangeEvent<HTMLSelectElement>, prevMemberSetId: number) => {
    const memberSetId = +(e.target.value);
    this.props.onAlternateMemberPopChange(memberSetId, prevMemberSetId);
    this.props.generateAlternateMemberships();
  }

  private disableAlias = () => !this.props.project.memberSetId2;

  private buildRecords = () => {
    const records: MemberWaffleToppings[] = this.state.memberData.map((sr) => {
      if (this.props.isZipProject
        && sr.projectOverrideMemberSetType === ProjectOverrideMemberSetType.Risk) {
        return;
      }

      return {
        [wafflesKeyColumnName]: sr.projectOverrideMemberSetType.toString(),
        MemberType: (() => {
          return (
            <label className="summary-tile__title">
              {sr.projectOverrideMemberSetType}
            </label>
          );
        })(),
        MemberPop: (() => {
          if (sr.projectOverrideMemberSetType === ProjectOverrideMemberSetType.Primary) {
            if (this.props.shared) {
              return (
                <QESTextInput
                  type="text"
                  className="form-control"
                  disabled={true}
                  value={this.props.project.memberSetName || ""}
                  testId="member-table__edit-project-primary-member-set-text-box"
                  label=""
                />
              );
            } else {
              if (this.props.projectMemberPopulations) {
                return (
                  <QESSelect
                    label="Member Population"
                    hideLabel={true}
                    className="form-control"
                    onChange={this.props.handleMemberPopChange}
                    testId="member-table__edit-project-primary-member-set-select"
                    disabled={this.props.disabled}
                    value={this.props.project.memberSetId ? this.props.project.memberSetId : ""}
                  >
                    <option key="0" value="0">Select a Member Population</option>
                    {this.props.project.memberSetId == -1 && <option key="-1" value="-1">Different Member Population Being Used</option>}
                    {this.props.projectMemberPopulations.map((option) => (
                      option.hasActivatedVersions ?
                        <option key={option.memberSetId} value={option.memberSetId}>{option.resource.name}</option> :
                        <option key={option.memberSetId} value={option.memberSetId} disabled={true}>{option.resource.name} - No Activated Versions</option>
                    ))}
                  </QESSelect>
                );
              }
            }
          } else if (sr.projectOverrideMemberSetType === ProjectOverrideMemberSetType.Secondary) {
            if (this.props.shared) {
              return (
                <QESTextInput
                  type="text"
                  className="form-control"
                  disabled={true}
                  value={this.props.project.memberSetName2 || ""}
                  testId="member-table__edit-project-secondary-member-set-text-box"
                  label=""
                />
              );
            } else {
              if (this.props.projectMemberPopulations) {
                return (
                  <QESSelect
                    label="Secondary Member Population"
                    hideLabel={true}
                    className="form-control"
                    onChange={this.props.handleMemberPopChange2}
                    testId="member-table__edit-project-secondary-member-set-select"
                    disabled={this.props.disabled}
                    value={this.props.project.memberSetId2 ? this.props.project.memberSetId2 : ""}
                  >
                    <option key="" value="">Select a Second Member Population</option>
                    {this.props.project.memberSetId2 == -1 && this.props.projectMemberPopulations.length > 0 && <option key="-1" value="-1">Different Second Member Population Being Used</option>}
                    {this.props.projectMemberPopulations.map((option) => {
                      return (
                        option.hasActivatedVersions ?
                          <option key={option.memberSetId} value={option.memberSetId}>{option.resource.name}</option> :
                          <option key={option.memberSetId} value={option.memberSetId} disabled={true}>{option.resource.name} - No Activated Versions</option>
                      )
                    })}
                  </QESSelect>
                );
              } else {
                return;
              }
            }
          } else if (sr.projectOverrideMemberSetType === ProjectOverrideMemberSetType.Alternate) {
            if (this.props.shared) {
              return (
                <QESTextInput
                  type="text"
                  className="form-control"
                  disabled={true}
                  value={this.props.project.editProjectOverrideMemberSets.filter((x) => x.memberSetId === this.state.memberData[this.state.memberData.indexOf(sr)].memberSetId)[0].memberSetName || ""}
                  testId="member-table__edit-project-alternate-member-set-text-box"
                  label=""
                />
              );
            } else {
              if (this.props.projectMemberPopulations) {
                const usedAlternateMemberSetIds = this.props.project.editProjectOverrideMemberSets.filter((oms) => oms.projectOverrideMemberSetType === ProjectOverrideMemberSetType.Alternate).map((oms) => oms.memberSetId);
                return (
                  <QESSelect
                    label="Alternate Member Population"
                    hideLabel={true}
                    className="form-control"
                    onChange={(e) => this.updateRowId(e, sr.memberSetId)}
                    value={this.state.memberData[this.state.memberData.indexOf(sr)].memberSetId
                      ? this.state.memberData[this.state.memberData.indexOf(sr)].memberSetId
                      : "0"}
                    disabled={this.props.disabled}
                    testId="member-table__edit-project-alternate-member-set-select"
                  >
                    <option key="0" value="0" disabled={sr.memberSetId === null ? false : true}>Select an Alternate Member Population</option>
                    {this.props.projectMemberPopulations.map((option) => {
                      const optionIsUsedAndNotThisRow = usedAlternateMemberSetIds.includes(option.memberSetId) && this.state.memberData[this.state.memberData.indexOf(sr)].memberSetId !== option.memberSetId;
                      return (
                        option.hasActivatedVersions ?
                          <option key={option.memberSetId} value={option.memberSetId} disabled={optionIsUsedAndNotThisRow}>{option.resource.name}</option> :
                          <option key={option.memberSetId} value={option.memberSetId} disabled={true}>{option.resource.name} - No Activated Versions</option>
                      );
                    })}
                  </QESSelect>
                );
              }
            }
          } else if (sr.projectOverrideMemberSetType === ProjectOverrideMemberSetType.Risk) {
            if (this.props.shared) {
              return (
                <QESTextInput
                  type="text"
                  className="form-control"
                  disabled={true}
                  value={this.props.project.membershipRiskMemberSetAlias || ""}
                  testId="member-table__edit-project-risk-analysis-text-box"
                  label=""
                />
              );
            } else {
              if (this.props.user.roles.some((r) => r === Roles.MEMBERSHIP_RISK_USER) && this.props.projectMemberPopulations) {
                return (
                  <QESSelect
                    label="Risk Analysis Member Population"
                    hideLabel={true}
                    className="form-control"
                    onChange={this.props.handleRiskPopChange}
                    testId="member-table__edit-project-risk-analysis-select"
                    disabled={this.props.disabled}
                    value={this.props.project.membershipRiskMemberSetId ? this.props.project.membershipRiskMemberSetId : ""}
                  >
                    <option key="0" value="0">Select a Risk Analysis Member Population</option>
                    {this.props.projectMemberPopulations.map((option) => (
                      option.hasActivatedVersions ?
                        <option key={option.memberSetId} value={option.memberSetId}>{option.resource.name}</option> :
                        <option key={option.memberSetId} value={option.memberSetId} disabled={true}>{option.resource.name} - No Activated Versions</option>
                    ))}
                  </QESSelect>
                );
              }
            }
          }
        })(),
        MemberAlias: (() => {
          const otherAliases = new Set(this.props.project.editProjectOverrideMemberSets.filter((oms) => oms.memberSetId !== sr.memberSetId).map((oms) => oms.alias));
          if (sr.projectOverrideMemberSetType === ProjectOverrideMemberSetType.Primary) {
            return (
              <div>
                <QESTextInput
                  className="form-control"
                  disabled={this.props.disabled}
                  size={25}
                  value={this.props.project.memberSetAlias == "-1" ? "Different Member Alias Being Used" : this.props.project.memberSetAlias || ""}
                  testId="member-table__edit-project-primary-member-set-type-alias-input"
                  onChange={this.props.handleMemberPopAliasChange}
                  label=""
                  hideLabel={true}
                  style={{ padding: 0 }}
                  unrequiredValidation={true}
                  isValid={!sr.alias || !otherAliases.has(sr.alias)}
                  wasFocusedExternally={true}
                />
              </div>
            );
          } else if (sr.projectOverrideMemberSetType === ProjectOverrideMemberSetType.Secondary) {
            return (
              <div>
                <QESTextInput
                  className="form-control"
                  disabled={this.props.disabled || this.disableAlias()}
                  size={25}
                  value={this.props.project.memberSetAlias2 == "-1" ? "Different Member Alias Being Used" : this.props.project.memberSetAlias2 || ""}
                  testId="member-table__edit-project-secondary-member-set-type-alias-input"
                  onChange={this.props.handleMemberPopAliasChange2}
                  label=""
                  hideLabel={true}
                  style={{ padding: 0 }}
                  unrequiredValidation={true}
                  isValid={!sr.alias || !otherAliases.has(sr.alias)}
                  wasFocusedExternally={true}
                />
              </div>
            );
          } else if (sr.projectOverrideMemberSetType === ProjectOverrideMemberSetType.Alternate) {
            return (
              <div>
                <QESTextInput
                  className="form-control"
                  disabled={this.props.disabled}
                  maxLength={255}
                  value={sr.alias || ""}
                  testId="member-table__edit-project-alternate-member-set-type-alias-input"
                  onChange={(e) => this.updateRowAlias(e.target.value, sr)}
                  label="Alias"
                  hideLabel={true}
                  style={{ padding: 0 }}
                  unrequiredValidation={true}
                  isValid={!sr.alias || !otherAliases.has(sr.alias)}
                  wasFocusedExternally={true}
                />
              </div>
            );
          }
        })(),
        Delete: (() => (
          <div style={{ minWidth: "1rem" }}>
            {(!this.props.shared && sr.projectOverrideMemberSetType === ProjectOverrideMemberSetType.Alternate) && (
              <QESButton
                qesType="secondary-danger"
                className="pt-2 p-0"
                qesTestId="member-table__edit-project-delete-alternate-member-set-type-alias-button"
                onClick={() => this.deleteMemberRow(sr)}
              >
                <i className="fa fa-trash fa-lg text-danger" style={{ lineHeight: "1em" }} />
              </QESButton>
            )}
          </div>
        ))(),
      };
    });

    return records.filter((r) => !!r);
  }
}

const WrappedMemberTable = withUserContext(MemberTable);

export { WrappedMemberTable as MemberTable };
